// pages/Admin/UserManagement.tsx
import React, { useState, useEffect, useMemo } from 'react';
import {
    Container,
    Row,
    Col,
    Card,
    CardBody,
    CardHeader,
    CardTitle,
    Button,
    Badge,
    Form,
    FormGroup,
    Label,
    Input,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Alert,
    Spinner,
    InputGroup,
    InputGroupText
} from 'reactstrap';
import BreadCrumb from '../../Components/Common/BreadCrumb';
import TableContainer from '../../Components/Common/TableContainer';
import {
    Plus,
    Search,
    CheckCircle,
    AlertCircle,
    RefreshCw,
    User
} from 'react-feather';
import {
    GetAllUsers,
    CreateUser,
    UpdateUser,
    DeleteUser,
    ToggleUserStatus,
    GetUserStats,
    AdminChangePassword
} from '../../api/admin';
import UserActionDropdown from 'Components/useractiondropdown';

interface Users {
    id: number;
    username: string;
    email: string;
    role: boolean;
    isActive: boolean;
    lastLogin?: string;
    createdAt: string;
    updatedAt: string;
    _count?: {
        refreshTokens: number;
    };
}

interface UserFormData {
    username: string;
    email: string;
    password: string;
    confirmPassword: string;
    role: boolean;
}

interface PasswordChangeData {
    newPassword: string;
    confirmPassword: string;
}

interface UserStats {
    totalUsers: number;
    activeUsers: number;
    inactiveUsers: number;
    adminUsers: number;
    regularUsers: number;
    usersWithSessions: number;
}

const UserManagement = () => {
    // State
    const [users, setUsers] = useState<Users[]>([]);
    const [selectedUser, setSelectedUser] = useState<Users | null>(null);
    const [stats, setStats] = useState<UserStats | null>(null);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // Modal states
    const [userModal, setUserModal] = useState(false);
    const [deleteModal, setDeleteModal] = useState(false);
    const [passwordModal, setPasswordModal] = useState(false);

    // Form modes
    const [formMode, setFormMode] = useState<'add' | 'edit'>('add');

    // Search and filter
    const [searchTerm, setSearchTerm] = useState('');
    const [roleFilter, setRoleFilter] = useState<string>('all');
    const [statusFilter, setStatusFilter] = useState<string>('all');

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);

    // Form state
    const [formData, setFormData] = useState<UserFormData>({
        username: '',
        email: '',
        password: '',
        confirmPassword: '',
        role: false
    });

    const [passwordData, setPasswordData] = useState<PasswordChangeData>({
        newPassword: '',
        confirmPassword: ''
    });
    const [activeFilters, setActiveFilters] = useState({
        page: 1,
        limit: 10
    });
    // Form errors
    const [formErrors, setFormErrors] = useState<Partial<UserFormData>>({});
    const [passwordErrors, setPasswordErrors] = useState<Partial<PasswordChangeData>>({});

    // Fetch users and stats on mount
    useEffect(() => {
        fetchUsers();
        fetchStats();
    }, [currentPage, roleFilter, statusFilter, searchTerm]);

    // Handle success/error timeouts
    useEffect(() => {
        if (success) {
            const timer = setTimeout(() => setSuccess(''), 3000);
            return () => clearTimeout(timer);
        }
    }, [success]);

    useEffect(() => {
        if (error) {
            const timer = setTimeout(() => setError(''), 5000);
            return () => clearTimeout(timer);
        }
    }, [error]);

    // Populate form when editing
    useEffect(() => {
        if (selectedUser && formMode === 'edit') {
            setFormData({
                username: selectedUser.username || '',
                email: selectedUser.email,
                password: '',
                confirmPassword: '',
                role: selectedUser.role
            });
        }
    }, [selectedUser, formMode]);

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const response = await GetAllUsers({
                page: currentPage,
                limit: itemsPerPage,
                search: searchTerm || undefined,
                role: roleFilter !== 'all' ? roleFilter === 'true' : undefined,
                isActive: statusFilter !== 'all' ? statusFilter === 'true' : undefined
            });

            if (response.success) {
                setUsers(response.data);
            } else {
                setError('Failed to load users');
            }
        } catch (err: any) {
            setError(err.message || 'Failed to load users');
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const response = await GetUserStats();
            if (response.success) {
                setStats(response.data);
            }
        } catch (err) {
            console.error('Failed to load stats:', err);
        }
    };

    // Validate user form
    const validateUserForm = (): boolean => {
        const errors: Partial<UserFormData> = {};

        if (!formData.username.trim()) {
            errors.username = 'Username is required';
        } else if (formData.username.length < 3) {
            errors.username = 'Username must be at least 3 characters';
        }

        if (!formData.email.trim()) {
            errors.email = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            errors.email = 'Invalid email format';
        }

        if (formMode === 'add') {
            if (!formData.password) {
                errors.password = 'Password is required';
            } else if (formData.password.length < 6) {
                errors.password = 'Password must be at least 6 characters';
            }

            if (!formData.confirmPassword) {
                errors.confirmPassword = 'Please confirm password';
            } else if (formData.password !== formData.confirmPassword) {
                errors.confirmPassword = 'Passwords do not match';
            }
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    // Validate password form
    const validatePasswordForm = (): boolean => {
        const errors: Partial<PasswordChangeData> = {};

        if (!passwordData.newPassword) {
            errors.newPassword = 'New password is required';
        } else if (passwordData.newPassword.length < 6) {
            errors.newPassword = 'Password must be at least 6 characters';
        }

        if (!passwordData.confirmPassword) {
            errors.confirmPassword = 'Please confirm password';
        } else if (passwordData.newPassword !== passwordData.confirmPassword) {
            errors.confirmPassword = 'Passwords do not match';
        }

        setPasswordErrors(errors);
        return Object.keys(errors).length === 0;
    };

    // Handle form submit
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateUserForm()) {
            return;
        }

        setActionLoading(true);
        setError('');
        setSuccess('');

        try {
            let response;

            if (formMode === 'add') {
                response = await CreateUser({
                    username: formData.username,
                    email: formData.email,
                    password: formData.password,
                    role: formData.role
                });
            } else {
                response = await UpdateUser(selectedUser!.id, {
                    username: formData.username,
                    email: formData.email,
                    role: formData.role
                });
            }

            if (response.success) {
                setSuccess(formMode === 'add' ? 'User created successfully' : 'User updated successfully');
                setUserModal(false);
                resetForm();
                fetchUsers();
                fetchStats();
            } else {
                setError(response.message || 'Operation failed');
            }
        } catch (err: any) {
            setError(err.message || 'Operation failed');
        } finally {
            setActionLoading(false);
        }
    };

    // Handle password change
    const handlePasswordChange = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validatePasswordForm()) {
            return;
        }

        setActionLoading(true);
        setError('');
        setSuccess('');

        try {
            const response = await AdminChangePassword(selectedUser!.id, {
                newPassword: passwordData.newPassword,
                confirmPassword: passwordData.confirmPassword
            });

            if (response.success) {
                setSuccess('Password changed successfully');
                setPasswordModal(false);
                setPasswordData({ newPassword: '', confirmPassword: '' });
            } else {
                setError(response.message || 'Failed to change password');
            }
        } catch (err: any) {
            setError(err.message || 'Failed to change password');
        } finally {
            setActionLoading(false);
        }
    };

    // Handle delete
    const handleDelete = async () => {
        if (!selectedUser) return;

        setActionLoading(true);
        setError('');
        setSuccess('');

        try {
            const response = await DeleteUser(selectedUser.id);

            if (response.success) {
                setSuccess('User deleted successfully');
                setDeleteModal(false);
                fetchUsers();
                fetchStats();
            } else {
                setError(response.message || 'Failed to delete user');
            }
        } catch (err: any) {
            setError(err.message || 'Failed to delete user');
        } finally {
            setActionLoading(false);
        }
    };

    // Handle toggle status
    const handleToggleStatus = async (user: Users) => {
        setActionLoading(true);
        setError('');
        setSuccess('');

        try {
            const response = await ToggleUserStatus(user.id);

            if (response.success) {
                setSuccess(`User ${response.data.isActive ? 'activated' : 'deactivated'} successfully`);
                fetchUsers();
                fetchStats();
            } else {
                setError(response.message || 'Failed to toggle user status');
            }
        } catch (err: any) {
            setError(err.message || 'Failed to toggle user status');
        } finally {
            setActionLoading(false);
        }
    };

    // Reset form
    const resetForm = () => {
        setFormData({
            username: '',
            email: '',
            password: '',
            confirmPassword: '',
            role: false
        });
        setFormErrors({});
        setSelectedUser(null);
    };

    // Open add modal
    const openAddModal = () => {
        setFormMode('add');
        resetForm();
        setUserModal(true);
    };

    // Open edit modal
    const openEditModal = (user: Users) => {
        setFormMode('edit');
        setSelectedUser(user);
        setUserModal(true);
    };

    // Open password modal
    const openPasswordModal = (user: Users) => {
        setSelectedUser(user);
        setPasswordData({ newPassword: '', confirmPassword: '' });
        setPasswordErrors({});
        setPasswordModal(true);
    };

    // Open delete modal
    const openDeleteModal = (user: Users) => {
        setSelectedUser(user);
        setDeleteModal(true);
    };

    // Format date
    const formatDate = (dateString?: string) => {
        if (!dateString) return 'Never';
        return new Date(dateString).toLocaleString();
    };

    // Table columns
    const columns = useMemo(
        () => [
            {
                header: 'SI.No',
                accessorKey: 'id', // Keep this for sorting, but we'll override the cell
                enableColumnFilter: false,
                enableSorting: false, // Usually you don't want to sort by SI.No
                cell: (cell: any) => {
                    // Calculate the SI.No based on current page and items per page
                    const currentPage = activeFilters.page || 1;
                    const itemsPerPage = activeFilters.limit || 10;
                    const rowIndex = cell.row.index;

                    // SI.No = (currentPage - 1) * itemsPerPage + rowIndex + 1
                    const serialNumber = (currentPage - 1) * itemsPerPage + rowIndex + 1;

                    return (
                        <span className="fw-semibold">
                            {serialNumber}
                        </span>
                    );
                },
            },
            {
                header: 'User',
                accessorKey: 'username',
                cell: (cell: any) => (
                    <div>
                        <div className="fw-semibold">{cell.getValue() || 'N/A'}</div>
                        <small className="text-muted">{cell.row.original.email}</small>
                    </div>
                ),
            },
            {
                header: 'Role',
                accessorKey: 'role',
                cell: (cell: any) => (
                    <Badge color={cell.getValue() ? 'primary' : 'secondary'}>
                        {cell.getValue() ? 'Admin' : 'User'}
                    </Badge>
                ),
            },
            {
                header: 'Status',
                accessorKey: 'isActive',
                cell: (cell: any) => (
                    <Badge color={cell.getValue() ? 'success' : 'danger'}>
                        {cell.getValue() ? 'Active' : 'Inactive'}
                    </Badge>
                ),
            },
            {
                header: 'Last Login',
                accessorKey: 'lastLogin',
                cell: (cell: any) => formatDate(cell.getValue()),
            },
            {
                header: 'Sessions',
                accessorKey: '_count.refreshTokens',
                cell: (cell: any) => (
                    <Badge color="info" pill>
                        {cell.getValue() || 0}
                    </Badge>
                ),
            },
            {
                header: 'Actions',
                accessorKey: 'actions',
                cell: (cell: any) => {
                    const user = cell.row.original;
                    return (
                        <UserActionDropdown
                            user={user}
                            onEditClick={openEditModal}
                            onPasswordClick={openPasswordModal}
                            onToggleStatus={handleToggleStatus}
                            onDeleteClick={(id) => {
                                const userToDelete = cell.row.original;
                                openDeleteModal(userToDelete);
                            }}
                        />
                    );
                },
            },
        ],
        [openEditModal, openPasswordModal, handleToggleStatus, openDeleteModal, activeFilters] // Add activeFilters to dependencies
    );
    // Filtered users (for search)
    const filteredUsers = users.filter(user => {
        const matchesSearch = !searchTerm ||
            user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.email.toLowerCase().includes(searchTerm.toLowerCase());

        return matchesSearch;
    });

    return (
        <div className="page-content">
            <Container fluid>
                <BreadCrumb title="User Management" pageTitle="Admin" />

                {/* Alerts */}
                <Row className="mb-3">
                    <Col>
                        {success && (
                            <Alert color="success" className="alert-dismissible fade show">
                                <CheckCircle size={18} className="me-2" />
                                {success}
                                <button type="button" className="btn-close" onClick={() => setSuccess('')}></button>
                            </Alert>
                        )}
                        {error && (
                            <Alert color="danger" className="alert-dismissible fade show">
                                <AlertCircle size={18} className="me-2" />
                                {error}
                                <button type="button" className="btn-close" onClick={() => setError('')}></button>
                            </Alert>
                        )}
                    </Col>
                </Row>

                {/* Stats Cards */}
                {stats && (
                    <Row className="mb-4">
                        <Col xl={2} md={4}>
                            <Card className="bg-primary bg-gradient text-white">
                                <CardBody>
                                    <h5 className="text-white">Total Users</h5>
                                    <h2 className="mb-0">{stats.totalUsers}</h2>
                                </CardBody>
                            </Card>
                        </Col>
                        <Col xl={2} md={4}>
                            <Card className="bg-success bg-gradient text-white">
                                <CardBody>
                                    <h5 className="text-white">Active</h5>
                                    <h2 className="mb-0">{stats.activeUsers}</h2>
                                </CardBody>
                            </Card>
                        </Col>
                        <Col xl={2} md={4}>
                            <Card className="bg-danger bg-gradient text-white">
                                <CardBody>
                                    <h5 className="text-white">Inactive</h5>
                                    <h2 className="mb-0">{stats.inactiveUsers}</h2>
                                </CardBody>
                            </Card>
                        </Col>
                        <Col xl={2} md={4}>
                            <Card className="bg-info bg-gradient text-white">
                                <CardBody>
                                    <h5 className="text-white">Admins</h5>
                                    <h2 className="mb-0">{stats.adminUsers}</h2>
                                </CardBody>
                            </Card>
                        </Col>
                        <Col xl={2} md={4}>
                            <Card className="bg-warning bg-gradient text-white">
                                <CardBody>
                                    <h5 className="text-white">Regular</h5>
                                    <h2 className="mb-0">{stats.regularUsers}</h2>
                                </CardBody>
                            </Card>
                        </Col>
                        <Col xl={2} md={4}>
                            <Card className="bg-secondary bg-gradient text-white">
                                <CardBody>
                                    <h5 className="text-white">Active Sessions</h5>
                                    <h2 className="mb-0">{stats.usersWithSessions}</h2>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                )}

                {/* Main Card */}
                <Row>
                    <Col lg={12}>
                        <Card>
                            <CardHeader className="d-flex justify-content-between align-items-center">
                                <CardTitle className="mb-0">User Management</CardTitle>
                                <div className="d-flex gap-2">
                                    <div className="search-box" style={{ width: '250px' }}>
                                        <InputGroup>
                                            <InputGroupText>
                                                <Search size={16} />
                                            </InputGroupText>
                                            <Input
                                                type="text"
                                                placeholder="Search users..."
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                            />
                                        </InputGroup>
                                    </div>
                                    <Input
                                        type="select"
                                        value={roleFilter}
                                        onChange={(e) => setRoleFilter(e.target.value)}
                                        style={{ width: '120px' }}
                                    >
                                        <option value="all">All Roles</option>
                                        <option value="true">Admin</option>
                                        <option value="false">User</option>
                                    </Input>
                                    <Input
                                        type="select"
                                        value={statusFilter}
                                        onChange={(e) => setStatusFilter(e.target.value)}
                                        style={{ width: '120px' }}
                                    >
                                        <option value="all">All Status</option>
                                        <option value="true">Active</option>
                                        <option value="false">Inactive</option>
                                    </Input>
                                    <Button color="primary" onClick={openAddModal}>
                                        <Plus size={16} className="me-1" />
                                        Add User
                                    </Button>
                                    <Button color="light" onClick={fetchUsers}>
                                        <RefreshCw size={16} />
                                    </Button>
                                </div>
                            </CardHeader>

                            <CardBody>
                                {loading ? (
                                    <div className="text-center py-5">
                                        <Spinner color="primary" />
                                        <p className="mt-2">Loading users...</p>
                                    </div>
                                ) : filteredUsers.length === 0 ? (
                                    <div className="text-center py-5">
                                        <User size={48} className="text-muted mb-3" />
                                        <h5>No users found</h5>
                                        <p className="text-muted">
                                            {searchTerm ? 'Try a different search term' : 'Get started by adding your first user'}
                                        </p>
                                        {!searchTerm && (
                                            <Button color="primary" onClick={openAddModal}>
                                                <Plus size={16} className="me-1" />
                                                Add First User
                                            </Button>
                                        )}
                                    </div>
                                ) : (
                                    <>
                                        <TableContainer
                                            columns={columns}
                                            data={filteredUsers}
                                            // isGlobalFilter={false}
                                            customPageSize={itemsPerPage}
                                            tableClass="table-striped"
                                            theadClass="table-light"
                                        />

                                    </>
                                )}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>

                {/* Add/Edit User Modal */}
                <Modal isOpen={userModal} toggle={() => { setUserModal(false); resetForm(); }} size="md">
                    <ModalHeader toggle={() => { setUserModal(false); resetForm(); }}>
                        {formMode === 'add' ? 'Add New User' : 'Edit User'}
                    </ModalHeader>
                    <Form onSubmit={handleSubmit}>
                        <ModalBody>
                            <Row>
                                <Col md={12}>
                                    <FormGroup className="mb-3">
                                        <Label for="username">
                                            Username <span className="text-danger">*</span>
                                        </Label>
                                        <Input
                                            type="text"
                                            id="username"
                                            value={formData.username}
                                            onChange={(e) => {
                                                setFormData({ ...formData, username: e.target.value });
                                                if (formErrors.username) {
                                                    setFormErrors({ ...formErrors, username: undefined });
                                                }
                                            }}
                                            invalid={!!formErrors.username}
                                            disabled={actionLoading}
                                        />
                                        {formErrors.username && (
                                            <div className="invalid-feedback d-block">{formErrors.username}</div>
                                        )}
                                    </FormGroup>
                                </Col>

                                <Col md={12}>
                                    <FormGroup className="mb-3">
                                        <Label for="email">
                                            Email <span className="text-danger">*</span>
                                        </Label>
                                        <Input
                                            type="email"
                                            id="email"
                                            value={formData.email}
                                            onChange={(e) => {
                                                setFormData({ ...formData, email: e.target.value });
                                                if (formErrors.email) {
                                                    setFormErrors({ ...formErrors, email: undefined });
                                                }
                                            }}
                                            invalid={!!formErrors.email}
                                            disabled={actionLoading}
                                        />
                                        {formErrors.email && (
                                            <div className="invalid-feedback d-block">{formErrors.email}</div>
                                        )}
                                    </FormGroup>
                                </Col>

                                {formMode === 'add' && (
                                    <>
                                        <Col md={12}>
                                            <FormGroup className="mb-3">
                                                <Label for="password">
                                                    Password <span className="text-danger">*</span>
                                                </Label>
                                                <Input
                                                    type="password"
                                                    id="password"
                                                    value={formData.password}
                                                    onChange={(e) => {
                                                        setFormData({ ...formData, password: e.target.value });
                                                        if (formErrors.password) {
                                                            setFormErrors({ ...formErrors, password: undefined });
                                                        }
                                                    }}
                                                    invalid={!!formErrors.password}
                                                    disabled={actionLoading}
                                                />
                                                {formErrors.password && (
                                                    <div className="invalid-feedback d-block">{formErrors.password}</div>
                                                )}
                                            </FormGroup>
                                        </Col>

                                        <Col md={12}>
                                            <FormGroup className="mb-3">
                                                <Label for="confirmPassword">
                                                    Confirm Password <span className="text-danger">*</span>
                                                </Label>
                                                <Input
                                                    type="password"
                                                    id="confirmPassword"
                                                    value={formData.confirmPassword}
                                                    onChange={(e) => {
                                                        setFormData({ ...formData, confirmPassword: e.target.value });
                                                        if (formErrors.confirmPassword) {
                                                            setFormErrors({ ...formErrors, confirmPassword: undefined });
                                                        }
                                                    }}
                                                    invalid={!!formErrors.confirmPassword}
                                                    disabled={actionLoading}
                                                />
                                                {formErrors.confirmPassword && (
                                                    <div className="invalid-feedback d-block">{formErrors.confirmPassword}</div>
                                                )}
                                            </FormGroup>
                                        </Col>
                                    </>
                                )}

                                <Col md={12}>
                                    <FormGroup check className="mb-3">
                                        <Input
                                            type="checkbox"
                                            id="role"
                                            checked={formData.role}
                                            onChange={(e) => setFormData({ ...formData, role: e.target.checked })}
                                            disabled={actionLoading}
                                        />
                                        <Label check for="role">
                                            Grant Administrator Privileges
                                        </Label>
                                    </FormGroup>
                                </Col>
                            </Row>
                        </ModalBody>
                        <ModalFooter>
                            <Button
                                type="button"
                                color="light"
                                onClick={() => { setUserModal(false); resetForm(); }}
                                disabled={actionLoading}
                            >
                                Cancel
                            </Button>
                            <Button type="submit" color="primary" disabled={actionLoading}>
                                {actionLoading ? (
                                    <>
                                        <Spinner size="sm" className="me-2" />
                                        {formMode === 'add' ? 'Creating...' : 'Updating...'}
                                    </>
                                ) : (
                                    formMode === 'add' ? 'Create User' : 'Update User'
                                )}
                            </Button>
                        </ModalFooter>
                    </Form>
                </Modal>

                {/* Change Password Modal */}
                <Modal isOpen={passwordModal} toggle={() => setPasswordModal(false)} size="md">
                    <ModalHeader toggle={() => setPasswordModal(false)}>
                        Change Password - {selectedUser?.username}
                    </ModalHeader>
                    <Form onSubmit={handlePasswordChange}>
                        <ModalBody>
                            <Row>
                                <Col md={12}>
                                    <FormGroup className="mb-3">
                                        <Label for="newPassword">
                                            New Password <span className="text-danger">*</span>
                                        </Label>
                                        <Input
                                            type="password"
                                            id="newPassword"
                                            value={passwordData.newPassword}
                                            onChange={(e) => {
                                                setPasswordData({ ...passwordData, newPassword: e.target.value });
                                                if (passwordErrors.newPassword) {
                                                    setPasswordErrors({ ...passwordErrors, newPassword: undefined });
                                                }
                                            }}
                                            invalid={!!passwordErrors.newPassword}
                                            disabled={actionLoading}
                                        />
                                        {passwordErrors.newPassword && (
                                            <div className="invalid-feedback d-block">{passwordErrors.newPassword}</div>
                                        )}
                                    </FormGroup>
                                </Col>

                                <Col md={12}>
                                    <FormGroup className="mb-3">
                                        <Label for="confirmPassword">
                                            Confirm Password <span className="text-danger">*</span>
                                        </Label>
                                        <Input
                                            type="password"
                                            id="confirmPassword"
                                            value={passwordData.confirmPassword}
                                            onChange={(e) => {
                                                setPasswordData({ ...passwordData, confirmPassword: e.target.value });
                                                if (passwordErrors.confirmPassword) {
                                                    setPasswordErrors({ ...passwordErrors, confirmPassword: undefined });
                                                }
                                            }}
                                            invalid={!!passwordErrors.confirmPassword}
                                            disabled={actionLoading}
                                        />
                                        {passwordErrors.confirmPassword && (
                                            <div className="invalid-feedback d-block">{passwordErrors.confirmPassword}</div>
                                        )}
                                    </FormGroup>
                                </Col>
                            </Row>
                        </ModalBody>
                        <ModalFooter>
                            <Button
                                type="button"
                                color="light"
                                onClick={() => setPasswordModal(false)}
                                disabled={actionLoading}
                            >
                                Cancel
                            </Button>
                            <Button type="submit" color="warning" disabled={actionLoading}>
                                {actionLoading ? (
                                    <>
                                        <Spinner size="sm" className="me-2" />
                                        Changing...
                                    </>
                                ) : (
                                    'Change Password'
                                )}
                            </Button>
                        </ModalFooter>
                    </Form>
                </Modal>

                {/* Delete Confirmation Modal */}
                <Modal isOpen={deleteModal} toggle={() => setDeleteModal(false)} centered>
                    <ModalHeader toggle={() => setDeleteModal(false)}>
                        Confirm Delete
                    </ModalHeader>
                    <ModalBody>
                        <Alert color="danger" className="mb-0">
                            <AlertCircle size={18} className="me-2" />
                            <strong>Warning:</strong> This action cannot be undone.
                        </Alert>
                        <p className="mt-3">
                            Are you sure you want to delete user <strong>{selectedUser?.username}</strong>?
                            <br />
                            <small className="text-muted">
                                This will permanently remove the user and all associated data.
                            </small>
                        </p>
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            color="light"
                            onClick={() => setDeleteModal(false)}
                            disabled={actionLoading}
                        >
                            Cancel
                        </Button>
                        <Button
                            color="danger"
                            onClick={handleDelete}
                            disabled={actionLoading}
                        >
                            {actionLoading ? (
                                <>
                                    <Spinner size="sm" className="me-2" />
                                    Deleting...
                                </>
                            ) : (
                                'Delete User'
                            )}
                        </Button>
                    </ModalFooter>
                </Modal>
            </Container>
        </div>
    );
};

export default UserManagement;